import {
  type MutateOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { gql } from 'graphql-request';
import { gqlClient } from '@/lib';
import type { QueryError } from '@/utils/errors';
import { createProfileQueryKey } from '@/utils/queryKeys';
import type {
  AddOrganizationSeatPacksMutation,
  AddOrganizationSeatPacksMutationVariables,
} from './useAddSeatPacks.generated';

const query = gql`
  mutation AddOrganizationSeatPacks($input: AddOrganizationSeatPacksInput!) {
    addOrganizationSeatPacks(input: $input)
  }
`;

// Adds seat packs to a business org's subscription; the prorated charge
// happens immediately on the server.
export const useAddSeatPacks = (organizationId: string) => {
  const queryClient = useQueryClient();

  const { mutate, isPending } = useMutation<
    AddOrganizationSeatPacksMutation,
    QueryError,
    AddOrganizationSeatPacksMutationVariables
  >({
    mutationFn: (variables) => {
      return gqlClient.request<
        AddOrganizationSeatPacksMutation,
        AddOrganizationSeatPacksMutationVariables
      >(query, variables);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: createProfileQueryKey() });
    },
  });

  const addSeatPacks = (
    packs: number,
    options?: MutateOptions<
      AddOrganizationSeatPacksMutation,
      QueryError,
      AddOrganizationSeatPacksMutationVariables
    >
  ) => {
    return mutate({ input: { organizationId, packs } }, options);
  };

  return { addSeatPacks, isPending };
};
